import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { fmtDate } from "@/lib/datetime";
import { formatPhone, formatPhoneInput } from "@/lib/phone";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Trash2, Pencil, UserPlus, PackagePlus, Phone, MapPin, Search, X } from "lucide-react";
import PatientOrdersDialog from "@/components/PatientOrdersDialog";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const ITEM_OPTIONS = ["Regular Medication", "Controlled Medication", "Refrigerated", "Other"];
const DELIVER_TYPES = [
  { v: "same_day", label: "Same Day" },
  { v: "next_day", label: "Next Day" },
  { v: "otc", label: "OTC" },
  { v: "pickup", label: "Pick Up" },
];

function PatientFormDialog({ open, onOpenChange, initial, onSubmit, title, submitLabel }) {
  const [form, setForm] = useState({ name: "", phone: "", address: "", notes: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { setForm(initial || { name: "", phone: "", address: "", notes: "" }); }, [initial, open]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await onSubmit(form); onOpenChange(false); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3" data-testid="patient-form">
          <div><Label>Patient name</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="patient-name" /></div>
          <div><Label>Phone</Label><Input required value={form.phone} onChange={(e) => setForm({ ...form, phone: formatPhoneInput(e.target.value) })} placeholder="305-555-1234" data-testid="patient-phone" /></div>
          <div><Label>Address</Label><Input required value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="patient-address" /></div>
          <div><Label>Notes (optional)</Label><Textarea rows={2} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          <Button type="submit" disabled={busy} className="rounded-full w-full" data-testid="patient-submit">{busy ? "Saving…" : submitLabel}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CreateOrderFromPatient({ patient, orderNumberingMode, open, onOpenChange, onCreated }) {
  const { user } = useAuth();
  const canSetCopay = user?.role === "super_admin" || user?.role === "pharmacy_staff";
  const [items, setItems] = useState([{ name: "Regular Medication", qty: 1, notes: "" }]);
  const [deliverType, setDeliverType] = useState("same_day");
  const [priority, setPriority] = useState("normal");
  const [notes, setNotes] = useState("");
  const [copayEnabled, setCopayEnabled] = useState(false);
  const [copay, setCopay] = useState("");
  const [orderNumber, setOrderNumber] = useState("");
  const [busy, setBusy] = useState(false);
  const isManualNumbering = orderNumberingMode === "manual";

  useEffect(() => {
    if (open) {
      setItems([{ name: "Regular Medication", qty: 1, notes: "" }]);
      setDeliverType("same_day"); setPriority("normal");
      // Pull patient.notes into the order's Delivery notes so dispatchers
      // don't re-type recurring instructions.
      setNotes(patient?.notes || "");
      setCopayEnabled(false); setCopay("");
      setOrderNumber("");
    }
  }, [open, patient?.notes]);

  const setItem = (i, k, v) => setItems((a) => a.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));
  const addItem = () => setItems((a) => [...a, { name: "Regular Medication", qty: 1, notes: "" }]);
  const removeItem = (i) => setItems((a) => (a.length > 1 ? a.filter((_, idx) => idx !== i) : a));

  const submit = async (e) => {
    e.preventDefault();
    const cleaned = items.filter((it) => it.name.trim()).map((it) => ({ ...it, qty: Number(it.qty) || 1 }));
    if (!cleaned.length) { toast.error("Add at least one item"); return; }
    setBusy(true);
    try {
      const payload = {
        customer_name: patient.name, customer_phone: patient.phone, address: patient.address,
        pharmacy_id: patient.pharmacy_id, items: cleaned, priority, deliver_type: deliverType, notes: notes || null,
      };
      if (canSetCopay && copayEnabled && copay !== "") {
        const n = Number(copay);
        if (Number.isFinite(n) && n >= 0) payload.copay = n;
      }
      if (isManualNumbering) {
        const trimmed = (orderNumber || "").trim();
        if (trimmed) payload.order_number = trimmed;
      }
      await api.post("/orders", payload);
      toast.success(`Order created for ${patient.name}`);
      onCreated?.();
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="create-order-from-patient">
        <DialogHeader><DialogTitle>New order for {patient?.name}</DialogTitle></DialogHeader>
        <div className="space-y-1 mb-2 p-3 rounded-lg bg-secondary/40 text-sm">
          <div className="font-medium">{patient?.name}</div>
          <div className="text-muted-foreground flex items-center gap-1.5"><Phone className="h-3 w-3" />{formatPhone(patient?.phone)}</div>
          <div className="text-muted-foreground flex items-center gap-1.5"><MapPin className="h-3 w-3" />{patient?.address}</div>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Deliver type</Label>
              <Select value={deliverType} onValueChange={setDeliverType}>
                <SelectTrigger data-testid="new-order-deliver-type"><SelectValue /></SelectTrigger>
                <SelectContent>{DELIVER_TYPES.map((d) => <SelectItem key={d.v} value={d.v}>{d.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Priority</Label>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                  <SelectItem value="cold_chain">Cold Chain</SelectItem>
                  <SelectItem value="clinic">Clinic</SelectItem>
                  <SelectItem value="business">Business</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Items</Label>
              <Button type="button" variant="outline" size="sm" onClick={addItem}><Plus className="h-3.5 w-3.5 mr-1" /> Add</Button>
            </div>
            <div className="space-y-2">
              {items.map((it, i) => (
                <div key={i} className="grid grid-cols-12 gap-2">
                  <div className="col-span-7">
                    <Select value={it.name} onValueChange={(v) => setItem(i, "name", v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{ITEM_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <Input className="col-span-2" type="number" min={1} value={it.qty} onChange={(e) => setItem(i, "qty", e.target.value)} />
                  <Input className="col-span-2" placeholder="notes" value={it.notes} onChange={(e) => setItem(i, "notes", e.target.value)} />
                  <Button type="button" variant="ghost" size="icon" className="col-span-1" onClick={() => removeItem(i)}><X className="h-4 w-4" /></Button>
                </div>
              ))}
            </div>
          </div>
          <div>
            <Label>Delivery notes</Label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Apartment buzzer, allergies…" />
          </div>
          {isManualNumbering && (
            <div data-testid="manual-order-number-section">
              <Label>
                Order # <span className="text-muted-foreground text-xs font-normal">(this pharmacy uses manual numbering · leave blank to auto-assign)</span>
              </Label>
              <Input
                value={orderNumber}
                onChange={(e) => setOrderNumber(e.target.value)}
                placeholder="e.g. RX00123"
                maxLength={40}
                data-testid="manual-order-number-input"
              />
            </div>
          )}
          {canSetCopay && (
            <div data-testid="quick-copay-section">
              <Label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={copayEnabled}
                  onCheckedChange={(v) => { setCopayEnabled(!!v); if (!v) setCopay(""); }}
                  data-testid="quick-copay-toggle"
                />
                <span className="text-sm">Charge a copay</span>
              </Label>
              {copayEnabled && (
                <div className="relative mt-1.5">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={copay}
                    onChange={(e) => setCopay(e.target.value)}
                    placeholder="0.00"
                    className="pl-7"
                    data-testid="quick-copay"
                    autoFocus
                  />
                </div>
              )}
            </div>
          )}
          <Button type="submit" disabled={busy} className="rounded-full w-full" data-testid="new-order-submit">{busy ? "Creating…" : "Create order"}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function PharmacyStaffPatients() {
  const [patients, setPatients] = useState([]);
  const [search, setSearch] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [editPatient, setEditPatient] = useState(null);
  const [orderTarget, setOrderTarget] = useState(null);
  const [historyTarget, setHistoryTarget] = useState(null);
  // Track the staff user's own pharmacy so New Order knows if manual
  // order-numbering is enabled — used to conditionally show the Order #
  // input on the Create Order dialog.
  const [ownPharmacy, setOwnPharmacy] = useState(null);

  const load = () => api.get("/patients").then((r) => setPatients(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    // Pharmacy staff can see the pharmacy they belong to via /api/pharmacies
    // (backend already scopes the list to their assignment). Read the first
    // (and typically only) entry.
    api.get("/pharmacies").then((r) => setOwnPharmacy((r.data || [])[0] || null)).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return patients;
    return patients.filter((p) =>
      [p.name, p.phone, p.address, p.notes].some((v) => v && v.toLowerCase().includes(q))
    );
  }, [patients, search]);

  const createPatient = async (form) => { await api.post("/patients", form); toast.success("Patient added"); load(); };
  const updatePatient = async (form) => { await api.put(`/patients/${editPatient.id}`, form); toast.success("Patient updated"); setEditPatient(null); load(); };
  const deletePatient = async (p) => {
    if (!confirm(`Delete patient ${p.name}?`)) return;
    try { await api.delete(`/patients/${p.id}`); toast.success("Patient removed"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-6 lg:p-10 max-w-[1500px] mx-auto" data-testid="pharmacy-staff-patients-page">
      <header className="flex items-end justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl lg:text-4xl font-display font-bold tracking-tight">Patients</h1>
          <p className="text-muted-foreground mt-1.5">Everyone registered under your pharmacy.</p>
        </div>
        <Button className="rounded-full" onClick={() => setAddOpen(true)} data-testid="add-patient-btn">
          <UserPlus className="h-4 w-4 mr-1.5" /> Add patient
        </Button>
      </header>

      <PatientFormDialog open={addOpen} onOpenChange={setAddOpen} initial={null} onSubmit={createPatient} title="New patient" submitLabel="Add patient" />
      <PatientFormDialog open={!!editPatient} onOpenChange={(o) => !o && setEditPatient(null)}
        initial={editPatient ? { name: editPatient.name, phone: editPatient.phone, address: editPatient.address, notes: editPatient.notes || "" } : null}
        onSubmit={updatePatient} title="Edit patient" submitLabel="Save changes" />
      <CreateOrderFromPatient
        patient={orderTarget}
        orderNumberingMode={ownPharmacy?.order_numbering || "auto"}
        open={!!orderTarget}
        onOpenChange={(o) => !o && setOrderTarget(null)}
        onCreated={load}
      />
      <PatientOrdersDialog patient={historyTarget} open={!!historyTarget} onOpenChange={(o) => !o && setHistoryTarget(null)} />

      <Card className="rounded-xl border-border bg-card/80 overflow-hidden">
        <div className="px-5 py-3 bg-secondary/50 border-b border-border flex items-center justify-between gap-3 flex-wrap">
          <div className="text-xs uppercase tracking-wider font-medium text-muted-foreground">
            Patients ({filtered.length}{search ? ` / ${patients.length}` : ""})
          </div>
          <div className="relative w-full sm:w-96">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, phone, or address…"
              className="pl-8 h-9 text-sm"
              data-testid="staff-patient-search"
            />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="staff-patients-table">
            <thead className="text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr className="border-b border-border">
                <th className="px-5 py-2 font-medium">Patient</th>
                <th className="px-5 py-2 font-medium">Phone</th>
                <th className="px-5 py-2 font-medium">Address</th>
                <th className="px-5 py-2 font-medium">Orders</th>
                <th className="px-5 py-2 font-medium">Last order</th>
                <th className="px-5 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <tr key={p.id} className="border-b border-border hover:bg-secondary/30" data-testid={`patient-row-${p.id}`}>
                  <td className="px-5 py-3 font-medium">
                    <button
                      type="button"
                      onClick={() => setHistoryTarget(p)}
                      className="text-primary hover:underline underline-offset-2 text-left"
                      data-testid={`patient-name-link-${p.id}`}
                      title="View order history"
                    >
                      {p.name}
                    </button>
                  </td>
                  <td className="px-5 py-3 text-muted-foreground font-mono-num text-xs">{formatPhone(p.phone)}</td>
                  <td className="px-5 py-3 text-muted-foreground max-w-[320px] truncate">{p.address}</td>
                  <td className="px-5 py-3 font-mono-num text-xs">{p.total_orders || 0}</td>
                  <td className="px-5 py-3 text-xs text-muted-foreground">{p.last_order_at ? fmtDate(p.last_order_at) : "—"}</td>
                  <td className="px-3 py-3 text-right whitespace-nowrap">
                    <Button size="icon" onClick={() => setOrderTarget(p)} className="h-9 w-9 rounded-full bg-accent hover:bg-accent/90 text-accent-foreground" data-testid={`new-order-btn-${p.id}`} title="Create new order">
                      <PackagePlus className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => setEditPatient(p)} className="text-muted-foreground hover:text-primary" data-testid={`patient-edit-${p.id}`}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => deletePatient(p)} className="text-muted-foreground hover:text-destructive" data-testid={`patient-delete-${p.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={6} className="px-5 py-12 text-center text-muted-foreground text-sm">
                  {patients.length === 0
                    ? `No patients yet. Click "Add patient" to register your first one.`
                    : `No patients match "${search}".`}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
